import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { API_BASE_URL } from './api-base';

export type ControlSafeAccessLevel = 'VIEW' | 'EDIT' | 'MANAGE';

export interface ControlSafeVault {
  id: number;
  tenant_id: number;
  name: string;
  description?: string | null;
  visibility: 'PRIVATE' | 'TEAM';
  is_favorite?: number;
  created_by: number;
  created_at: string;
  updated_at?: string | null;
  item_count?: number;
  folder_count?: number;
}

export interface ControlSafeFolder {
  id: number;
  tenant_id: number;
  vault_id: number;
  parent_folder_id?: number | null;
  name: string;
  is_favorite?: number;
  order_index?: number;
  created_by: number;
  created_at: string;
  updated_at?: string | null;
  created_by_name?: string;
  item_count?: number;
  folder_count?: number;
}

export interface ControlSafeItem {
  id: number;
  tenant_id: number;
  vault_id: number;
  folder_id?: number | null;
  item_type: string;
  title: string;
  subtitle?: string | null;
  website_url?: string | null;
  username?: string | null;
  password_score?: number | null;
  notes_preview?: string | null;
  is_favorite: number;
  status: string;
  created_by: number;
  created_at: string;
  updated_at?: string | null;
  last_access_at?: string | null;
  expires_at?: string | null;
  created_by_name?: string;
  updated_by_name?: string;
  vault_name?: string;
  location_label?: string;
  shared_access?: boolean | number;
  access_level?: ControlSafeAccessLevel | null;
  can_edit?: boolean;
  can_share?: boolean;
  can_manage?: boolean;
  can_delete?: boolean;
}

export interface ControlSafeItemDetail extends ControlSafeItem {
  secret_ciphertext: string;
  secret_iv: string;
  secret_tag: string;
  crypto_alg?: string;
  key_version?: number;
}

export interface ControlSafeField {
  id: number;
  tenant_id: number;
  item_id: number;
  field_key: string;
  field_label: string;
  field_type: string;
  value_plain?: string | null;
  value_ciphertext?: string | null;
  value_iv?: string | null;
  value_tag?: string | null;
  order_index?: number;
  created_at: string;
}

export interface ControlSafeAttachment {
  id: number;
  tenant_id: number;
  item_id: number;
  file_name: string;
  file_path: string;
  file_mime?: string | null;
  file_size_bytes?: number | null;
  is_encrypted: number;
  file_cipher_iv?: string | null;
  file_cipher_tag?: string | null;
  key_version?: number | null;
  uploaded_by: number;
  uploaded_at: string;
}

export interface ControlSafeOverviewStats {
  protected_items: number;
  folders: number;
  shared: number;
  expiring_soon: number;
  security_score: number;
}

export interface ControlSafeAuditLog {
  id: number;
  tenant_id: number;
  user_id: number;
  vault_id?: number | null;
  item_id?: number | null;
  event_type: string;
  event_description?: string | null;
  ip_address?: string | null;
  user_agent?: string | null;
  created_at: string;
  user_name?: string | null;
  user_email?: string | null;
}

export interface ControlSafeItemHistoryResponse {
  entries: ControlSafeAuditLog[];
  page: number;
  page_size: number;
  total: number;
}

export interface ControlSafeShareGrantsResponse {
  emails: string[];
  recipients?: Array<{ email: string; access_level: ControlSafeAccessLevel }>;
  access_level?: ControlSafeAccessLevel;
  can_manage?: boolean;
  can_edit?: boolean;
  can_share?: boolean;
  can_delete?: boolean;
}

@Injectable({ providedIn: 'root' })
export class ControlSafeService {
  private readonly baseUrl = `${API_BASE_URL}/controlsafe`;

  constructor(private http: HttpClient) {}

  listVaults(tenantId: number, userId: number): Observable<ControlSafeVault[]> {
    const params = new HttpParams().set('tenant_id', tenantId).set('user_id', userId);
    return this.http.get<ControlSafeVault[]>(`${this.baseUrl}/vaults`, { params });
  }

  createVault(payload: { tenant_id: number; name: string; description?: string | null; created_by: number }) {
    return this.http.post<ControlSafeVault>(`${this.baseUrl}/vaults`, payload);
  }

  updateVault(vaultId: number, payload: { tenant_id: number; user_id: number; name: string; description?: string | null }) {
    return this.http.put(`${this.baseUrl}/vaults/${vaultId}`, payload);
  }

  updateVaultFavorite(vaultId: number, tenantId: number, userId: number, isFavorite: boolean) {
    return this.http.put(`${this.baseUrl}/vaults/${vaultId}/favorite`, {
      tenant_id: tenantId,
      user_id: userId,
      is_favorite: isFavorite
    });
  }

  deleteVault(vaultId: number, tenantId: number, userId: number) {
    const params = new HttpParams().set('tenant_id', tenantId).set('user_id', userId);
    return this.http.delete(`${this.baseUrl}/vaults/${vaultId}`, { params });
  }

  listFolders(tenantId: number, userId: number, vaultId: number, parentId?: number | null) {
    let params = new HttpParams().set('tenant_id', tenantId).set('user_id', userId).set('vault_id', vaultId);
    if (parentId !== undefined) {
      params = params.set('parent_id', parentId ?? '');
    }
    return this.http.get<ControlSafeFolder[]>(`${this.baseUrl}/folders`, { params });
  }

  createFolder(payload: {
    tenant_id: number;
    vault_id: number;
    parent_folder_id?: number | null;
    name: string;
    order_index?: number;
    created_by: number;
  }) {
    return this.http.post<ControlSafeFolder>(`${this.baseUrl}/folders`, payload);
  }

  updateFolder(folderId: number, payload: { tenant_id: number; user_id: number; name: string; order_index?: number }) {
    return this.http.put(`${this.baseUrl}/folders/${folderId}`, payload);
  }

  updateFolderFavorite(folderId: number, tenantId: number, userId: number, isFavorite: boolean) {
    return this.http.put(`${this.baseUrl}/folders/${folderId}/favorite`, {
      tenant_id: tenantId,
      user_id: userId,
      is_favorite: isFavorite
    });
  }

  deleteFolder(folderId: number, tenantId: number, userId: number) {
    const params = new HttpParams().set('tenant_id', tenantId).set('user_id', userId);
    return this.http.delete(`${this.baseUrl}/folders/${folderId}`, { params });
  }

  listItems(tenantId: number, userId: number, vaultId: number, folderId?: number | null, search?: string) {
    let params = new HttpParams().set('tenant_id', tenantId).set('user_id', userId).set('vault_id', vaultId);
    if (folderId !== undefined) {
      params = params.set('folder_id', folderId ?? '');
    }
    if (search) {
      params = params.set('search', search);
    }
    return this.http.get<ControlSafeItem[]>(`${this.baseUrl}/items`, { params });
  }

  listAccessibleItems(tenantId: number, userId: number) {
    const params = new HttpParams().set('tenant_id', tenantId).set('user_id', userId);
    return this.http.get<ControlSafeItem[]>(`${this.baseUrl}/items/overview`, { params });
  }

  getItem(itemId: number, tenantId: number, userId: number) {
    const params = new HttpParams().set('tenant_id', tenantId).set('user_id', userId);
    return this.http.get<ControlSafeItemDetail>(`${this.baseUrl}/items/${itemId}`, { params });
  }

  createItem(payload: any) {
    return this.http.post<ControlSafeItem>(`${this.baseUrl}/items`, payload);
  }

  updateItem(itemId: number, payload: any) {
    return this.http.put(`${this.baseUrl}/items/${itemId}`, payload);
  }

  updateItemFavorite(itemId: number, tenantId: number, userId: number, isFavorite: boolean) {
    return this.http.put(`${this.baseUrl}/items/${itemId}/favorite`, {
      tenant_id: tenantId,
      user_id: userId,
      is_favorite: isFavorite
    });
  }

  deleteItem(itemId: number, tenantId: number, userId: number) {
    const params = new HttpParams().set('tenant_id', tenantId).set('user_id', userId);
    return this.http.delete(`${this.baseUrl}/items/${itemId}`, { params });
  }

  listFields(itemId: number, tenantId: number, userId: number) {
    const params = new HttpParams().set('tenant_id', tenantId).set('user_id', userId);
    return this.http.get<ControlSafeField[]>(`${this.baseUrl}/items/${itemId}/fields`, { params });
  }

  createField(itemId: number, payload: any) {
    return this.http.post<ControlSafeField>(`${this.baseUrl}/items/${itemId}/fields`, payload);
  }

  updateField(fieldId: number, payload: any) {
    return this.http.put(`${this.baseUrl}/fields/${fieldId}`, payload);
  }

  deleteField(fieldId: number, tenantId: number, userId: number) {
    const params = new HttpParams().set('tenant_id', tenantId).set('user_id', userId);
    return this.http.delete(`${this.baseUrl}/fields/${fieldId}`, { params });
  }

  listAttachments(itemId: number, tenantId: number, userId: number) {
    const params = new HttpParams().set('tenant_id', tenantId).set('user_id', userId);
    return this.http.get<ControlSafeAttachment[]>(`${this.baseUrl}/items/${itemId}/attachments`, { params });
  }

  listBanks() {
    return this.http.get<Array<{ code?: string; name: string }>>(`${this.baseUrl}/banks`);
  }

  getOverviewStats(tenantId: number, userId: number) {
    const params = new HttpParams().set('tenant_id', tenantId).set('user_id', userId);
    return this.http.get<ControlSafeOverviewStats>(`${this.baseUrl}/overview-stats`, { params });
  }

  getShareGrants(
    tenantId: number,
    userId: number,
    targetType: 'VAULT' | 'FOLDER' | 'ITEM',
    targetId: number
  ): Observable<ControlSafeShareGrantsResponse> {
    const params = new HttpParams()
      .set('tenant_id', tenantId)
      .set('user_id', userId)
      .set('target_type', targetType)
      .set('target_id', targetId);
    return this.http.get<ControlSafeShareGrantsResponse>(`${this.baseUrl}/share-grants`, { params });
  }

  saveShareGrants(payload: {
    tenant_id: number;
    user_id: number;
    target_type: 'VAULT' | 'FOLDER' | 'ITEM';
    target_id: number;
    emails?: string[];
    recipients?: Array<{ email: string; access_level: ControlSafeAccessLevel }>;
    access_level?: ControlSafeAccessLevel;
  }): Observable<{ success: boolean; emails: string[]; recipients?: Array<{ email: string; access_level: ControlSafeAccessLevel }>; access_level?: ControlSafeAccessLevel }> {
    return this.http.put<{ success: boolean; emails: string[]; recipients?: Array<{ email: string; access_level: ControlSafeAccessLevel }>; access_level?: ControlSafeAccessLevel }>(
      `${this.baseUrl}/share-grants`,
      payload
    );
  }

  listItemHistory(
    itemId: number,
    tenantId: number,
    userId: number,
    page = 1,
    pageSize = 5
  ): Observable<ControlSafeItemHistoryResponse> {
    const params = new HttpParams()
      .set('tenant_id', tenantId)
      .set('user_id', userId)
      .set('page', page)
      .set('page_size', pageSize);
    return this.http.get<ControlSafeItemHistoryResponse>(`${this.baseUrl}/items/${itemId}/history`, { params });
  }

  uploadAttachment(itemId: number, payload: { tenant_id: number; user_id: number; file: File }) {
    const formData = new FormData();
    formData.append('tenant_id', String(payload.tenant_id));
    formData.append('user_id', String(payload.user_id));
    formData.append('file', payload.file);
    return this.http.post<ControlSafeAttachment>(`${this.baseUrl}/items/${itemId}/attachments`, formData);
  }

  downloadAttachment(attachmentId: number, tenantId: number, userId: number) {
    const params = new HttpParams().set('tenant_id', tenantId).set('user_id', userId);
    return this.http.get(`${this.baseUrl}/attachments/${attachmentId}/download`, {
      params,
      responseType: 'blob'
    });
  }
}
